import {css} from "@cdx/ds";
import {useEffect, useRef, useState} from "react";
import {useSpring, animated} from "react-spring";

const useMousePosition = (cb: (pos: {x: number; y: number}) => void) => {
  const cbRef = useRef(cb);
  cbRef.current = cb;
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      cbRef.current({x: e.clientX, y: e.clientY});
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);
};

type Path = {
  width: number;
  height: number;
  d: string;
};

const paths: Path[] = [
  {
    width: 2400,
    height: 400,
    d: "M0 400h2400V52c-28.7-12.1-89 71.3-108.5 80.5-10 4.7-99.4-6.7-108.5-12-2.7-1.6-15.7-26.5-32-46.6-14-17.1-30.2-30.8-44.5-23.5-56 28.9-94.5 37.6-129 41.1-23 2.4-77.4-19.8-82-22.5-9.5-5.5-29-38.5-50.5-51s-62 120-101.5 129-121.5-30.2-126.5-31c-5-.8-45 10.5-47.5 20.3-2.5 9.9-32.5 23.7-70.5 19.7s-67.5-14.4-81-26c-13.5-11.5-67.4-20.4-95.5-21-25.5-.5-87.5-66.5-103.5-66.5-33.8 0-53-27.7-61-26.5-17.5 2.7-57.3 53-93.5 46.5-25-4.5-77.8-38.7-93.5-41.5-10.5-1.9-59 134.5-88 122.5-15.1-4.3-123.2 27-147.5 2.5-27.7-28-45.5-34.5-60.5-42.5s-56.2 35.3-74 40c-15.4 4-62-12.5-79-20s-47-51.3-55.5-54c-21-6.6-33 48.8-49 50-20 1.5-74.7 25.2-92.5 19.5-23.7-7.6-47.4-94.9-60-99.5-15.5-5.7-18-19-30.5-22-13 3.7-25 114.5-87.5 133-24 10.5-20-20-56-33S11.8 57.3 0 52v348Z",
  },
  {
    width: 2400,
    height: 400,
    d: "M131 46.2C77.8 69.4 21.5 61.7 0 52v348h2400V52c-61.5-26-131.5-26.4-241.5-8.7S2036 104.6 1932 59.5c-89.5-28-202 28-250.5 21s-256-45.5-326-45.5-221 40-291 27.5-100-17.7-110-20.5C898.5 26.2 690 58.4 648 69.5c-51.7 13.7-162-15.9-208.5-12.4s-91.9-22.8-114-31c-54.5-20-128-9-194.5 20Z",
  },
  {
    width: 2400,
    height: 400,
    d: "M149.5 83C96.3 106.2 21.5 55.7 0 46v354h2400V46c-5.8-2.5-61.5-33.2-171.5-15.5C2118.5 48.2 1806 144.1 1702 99a1830 1830 0 0 0-245.5-55.7c-48.5-7-221 19.2-291 19.2S960 176.5 890 164 788 90 755 76.5 677.5 36.9 635.5 48c-51.7 13.7-96.5 54.5-143 58s-144.9-71.7-167-79.8C271 6.2 216 54 149.5 83Z",
  },
];

const SvgLayer = ({
  y,
  scale = 1,
  color,
  currentColor,
  f,
  style,
  offsetX = 0,
  mirrored,
  pathIdx,
}: {
  y: number;
  scale?: number;
  color: string;
  currentColor: string;
  f: number;
  style: any;
  offsetX?: number;
  mirrored?: boolean;
  pathIdx: number;
}) => {
  const path = paths[pathIdx];
  const svg = `
    <svg
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 ${path.width} ${path.height}"
    >
      <defs>
        <linearGradient id="grad" gradientTransform="rotate(90)">
          <stop stop-color="${color}" offset="10%"/>
          <stop stop-color="${currentColor}" offset="${15 + 10 / scale}%"/>
        </linearGradient>
      </defs>
      <path d="${path.d}" fill="url(#grad)" ${mirrored ? `transform="scale(-1 1) translate(-100% 0)"` : ""}/>
    </svg>
  `;
  return (
    <animated.div
      className={css({position: "absolute"})}
      style={{
        bottom: "0%",
        left: "-100px",
        right: "-100px",
        height: "120%",
        maxHeight: "200px",
        backgroundImage: `url("data:image/svg+xml;base64,${btoa(svg)}")`,
        backgroundRepeat: "repeat-x",
        backgroundSize: `${path.width * scale}px ${path.height * scale}px`,
        backgroundPosition: `${offsetX}px 0`,
        transform: style.mouse.to(
          (mX: number, mY: number) =>
            `translate3d(${(-mX * f) / 15}px, ${(-mY * f) / 25 + y}px, 0) rotate(0.05deg)`
        ),
      }}
    />
  );
};

const JourneyZoneBg = ({initialOffset = 0}: {initialOffset?: number}) => {
  const [r] = useState<number>(() => 0.5 + Math.random() * 1);
  const [style, api] = useSpring(() => ({
    mouse: [0, 0],
    config: {tension: 30, friction: 15, mass: 10},
  }));
  useMousePosition(({x, y}) => {
    api.start({mouse: [x, y]});
  });
  return (
    <>
      <SvgLayer
        y={75}
        f={-0.05 * r}
        color="#2E6689"
        currentColor="#1C4664"
        scale={0.5}
        pathIdx={2}
        style={style}
        mirrored={r < 1}
        offsetX={initialOffset + 400 * r}
      />
      <SvgLayer
        y={90}
        f={0.05 * r}
        color="#71A2AD"
        currentColor="#C1F1FA"
        scale={0.8}
        pathIdx={2}
        style={style}
        mirrored={r > 1}
        offsetX={initialOffset + 400 * r}
      />
      <SvgLayer
        y={120}
        f={0.1 * r}
        color="#3B747F"
        currentColor="#9CD6E2"
        scale={0.6}
        pathIdx={1}
        style={style}
        mirrored={r < 1}
        offsetX={initialOffset + 400 * r}
      />
      <SvgLayer
        y={140}
        f={0.2 * r}
        color="#141E31"
        currentColor="#02060A"
        scale={0.6}
        pathIdx={1}
        style={style}
        mirrored={r > 1}
        offsetX={initialOffset}
      />
    </>
  );
};

export default JourneyZoneBg;
